import { Form } from 'antd';
import useSMSEmailSignature from 'modules/POS/Cashier/hooks/useSMSEmailSignature';
import { PaymentSocketData, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { IResponseData } from 'services/response';
import { Message } from 'stompjs';
import { removeBase64Prefix } from 'utils/removeBase64Prefix';
import storage from 'utils/sessionStorage';
import { setPageLoading } from 'widgets/PageLoading';
import CreditCardLoading, { ICreditPaymentLoadingRef } from '../../Card/ModalCardPayment/PaymentLoading';
import RequestSignature, { ISignatureRef } from '../../Card/ModalCardPayment/RequestSignature';
import { IFormMultiPaymentValues } from './helpers';
import { set } from 'lodash';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import { useAppDispatch } from 'store/hooks';
type Props = {
  value?: IFormMultiPaymentValues['detail'];
  onChange?: (value: IFormMultiPaymentValues['detail']) => void;
  billId?: string;
  onCCCompleted: (ignorePrint?: boolean) => void;
  onCreditCardCancel: () => void;
};
type Ref = {
  openCCLoading: (totalCC: number) => void;
  closeCCLoading: () => void;
  requestSignature: () => void;
  closeSignature: () => void;
};
export const useCreditCardPaymentHandlerRef = () => useRef<Ref>(null);
const CreditCardPaymentHandler = forwardRef<Ref, Props>(({ value: detail, billId, onCCCompleted }, ref) => {
  const masterForm = Form.useFormInstance();
  const creditCardLoadingRef = useRef<ICreditPaymentLoadingRef>(null);
  const signatureRef = useRef<ISignatureRef>(null);
  const SMSEmailSignature = useSMSEmailSignature();
  const socketContext = useSocketContext();
  const cashierMsg = cashierService.selectors.cashierMsg();
  const dispatch = useAppDispatch();

  const sendingData = (type: string, data?: any) => {
    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId: billId || '',
      actor: TypePaymentActors.CASHIER,
      data,
    };
    switch (type) {
      case TypePaymentPassData.REQUEST_SIGNATURE: {
        set(body, 'action', TypePaymentPassData.REQUEST_SIGNATURE);
        break;
      }
      case TypePaymentPassData.PAYMENT_PROCESSING: {
        set(body, 'action', TypePaymentPassData.PAYMENT_PROCESSING);
        break;
      }
      case TypePaymentPassData.PAYMENT_PROCESSING_FAIL: {
        set(body, 'action', TypePaymentPassData.PAYMENT_PROCESSING_FAIL);
        break;
      }
      default:
        break;
    }

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
  };

  const listeningData = (message: Message) => {
    if (!message.body) return;
    const payment: PaymentSocketData = JSON.parse(message.body);
    if (!payment) return;
    if (payment.billId !== billId) return;
    if (payment.actor !== TypePaymentActors.CUSTOMER) return;

    switch (payment.action) {
      case TypePaymentPassData.COMPLETED_PAYMENT: {
        signatureRef.current?.close();
        dispatch(cashierService.actions.saveCashierMessage(null));
        onCCCompleted();
        break;
      }
      case TypePaymentPassData.COMPLETED_PAYMENT_WITHOUT_RECEIPT: {
        signatureRef.current?.close();
        dispatch(cashierService.actions.saveCashierMessage(null));
        onCCCompleted(true);
        break;
      }
      case TypePaymentPassData.ON_CUSTOMER_SIGNATURE_CHANGE: {
        signatureRef.current?.setValue(payment.data);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!cashierMsg) return;
    listeningData(cashierMsg);
  }, [cashierMsg]);

  const requestSignature: Ref['requestSignature'] = () => {
    signatureRef.current?.open();
    sendingData(TypePaymentPassData.REQUEST_SIGNATURE);
  };

  const onSignatureDone = async (signatureValue: string) => {
    const billId = masterForm.getFieldValue(['detail', 'billId']);
    if (!billId) return;
    signatureRef.current?.close();
    setPageLoading(true);
    try {
      const res: IResponseData<boolean> = await cashierApis.updateSignature(billId || '', removeBase64Prefix(signatureValue));
      if (res.data.data) {
        onCCCompleted();
        return;
      }
    } catch (error) { }
    finally {
      setPageLoading(false);
    }
  };


  useImperativeHandle(ref, () => ({
    openCCLoading: (total) => {
      creditCardLoadingRef.current?.open(total, 120000);
      sendingData(TypePaymentPassData.PAYMENT_PROCESSING, { amount: total || 0 });
    },
    closeCCLoading: () => {
      creditCardLoadingRef.current?.close();
      sendingData(TypePaymentPassData.PAYMENT_PROCESSING_FAIL);
    },
    requestSignature,
    closeSignature: () => signatureRef.current?.close(),
  }));

  return (
    <>
      <CreditCardLoading ref={creditCardLoadingRef} />
      <RequestSignature
        ref={signatureRef}
        onAgree={onSignatureDone}
        onNoReceipt={SMSEmailSignature.onNoReceipt(billId || '', () => onCCCompleted(true))}
        onEmail={SMSEmailSignature.onEmail(billId || '', () => onCCCompleted(true))}
        onSMS={SMSEmailSignature.onSMS(billId || '', () => onCCCompleted(true))}
        defaultSMSPhone={detail?.customerPhone}
      />
    </>
  );
});
CreditCardPaymentHandler.displayName = 'CreditCardPaymentHandler';
export default CreditCardPaymentHandler;